import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    EyeIcon,
    HeartIcon,
    ShoppingBagIcon
} from '@heroicons/react/24/outline';
import {
    HeartIcon as HeartSolidIcon
} from '@heroicons/react/24/solid';
import { useAppContext } from '../context/AppContext';

const ProductCard = ({ product, onQuickView }) => {
    const navigate = useNavigate();
    const [isLiked, setIsLiked] = useState(false);
    const { addToCart } = useAppContext();
    const [isLoading, setIsLoading] = useState(false);

    const handleAddToCart = async (e) => {
        e.stopPropagation();
        setIsLoading(true);
        // Default to adding 1 quantity if no size needed, else open modal
        if (product.sizes && product.sizes.length > 0) {
            // If sizes are needed, we can either open modal or navigate to product page.
            // Keeping modal for add-to-cart flow for now as requested only for view button.
            // Alternatively, we could navigate: navigate(`/product/${product.id}`);
            onQuickView(product);
            setIsLoading(false);
            return;
        }
        await addToCart(product.id, 1);
        setIsLoading(false);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={{ y: -8 }}
            className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
            onClick={() => navigate(`/product/${product.id}`)}
        >
            <div className="relative aspect-[4/5] overflow-hidden bg-gray-100">
                <img
                    src={product.images?.[0] || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab'}
                    alt={product.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                {/* Overlay Actions */}
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4">
                    <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }}
                        className="bg-white text-gray-900 p-3 rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                    >
                        <EyeIcon className="h-5 w-5" />
                    </motion.button>
                    <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => { e.stopPropagation(); setIsLiked(!isLiked); }}
                        className="bg-white text-gray-900 p-3 rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                    >
                        {isLiked ? (
                            <HeartSolidIcon className="h-5 w-5 text-red-500" />
                        ) : (
                            <HeartIcon className="h-5 w-5" />
                        )}
                    </motion.button>
                </div>

                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                    {product.is_customizable && (
                        <span className="bg-white/90 backdrop-blur-sm text-gray-900 text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                            Customizable
                        </span>
                    )}
                    {product.is_new && (
                        <span className="bg-gray-900 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                            New
                        </span>
                    )}
                </div>
            </div>

            <div className="p-3">
                <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-900 text-base leading-tight group-hover:text-gray-600 transition-colors line-clamp-1">
                        {product.title}
                    </h3>
                    <p className="font-bold text-gray-900 text-sm">₹{product.price}</p>
                </div>
                <p className="text-xs text-gray-500 mb-3 line-clamp-3 break-words">
                    {product.description?.replace(/<[^>]*>/g, '') || 'No description available'}
                </p>

                <button
                    onClick={handleAddToCart}
                    disabled={isLoading}
                    className="w-full bg-gray-50 text-gray-900 font-semibold py-2 text-sm rounded-xl hover:bg-gray-900 hover:text-white transition-all duration-300 flex items-center justify-center gap-2 group/btn"
                >
                    {isLoading ? (
                        <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                    ) : (
                        <>
                            <ShoppingBagIcon className="h-4 w-4" />
                            <span>Add to Cart</span>
                        </>
                    )}
                </button>
            </div>
        </motion.div>
    );
};

export default ProductCard;
